/**
 * idleGate.ts — "is the player busy?" for background work (loading round 2).
 *
 * The boot pipeline (world build → shader warm-up → garage pre-warm → model
 * parsing) runs in slices, but a slice that starts the instant before a tap
 * still delays that tap by the slice's length — and on a COLD shader cache a
 * single compile slice is hundreds of milliseconds. MEASURED on production
 * (M4 Pro, real GPU, cold cache): a tap on QUICK RACE 400 ms after the
 * title landed behind a 4.1 s block — the NYC world's first draw compiling
 * its 102 programs — and the city select appeared 4.3 s later.
 *
 * This gate lets background work ask two things before each slice:
 *   - has the player touched anything in the last `quietMs`? (a tap is
 *     usually the start of a navigation — leave the thread alone)
 *   - did a screen just show? (`markBusy` — its entrance animation must
 *     not be stalled by a slice landing mid-flight: the "jumps abruptly")
 * `foreground` jobs (a race launch under its own opaque splash) bypass it —
 * they ARE what the player is waiting for.
 *
 * Pure bookkeeping; the DOM listeners are installed once by main.ts.
 */

let lastInputAt = -1e9;
let busyUntil = 0;
let foreground = 0;
let raceActive = false;
let installed = false;

function now(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

/** Note player input (installed on the document, capture phase, passive). */
export function noteInput(): void {
  lastInputAt = now();
}

/** A screen just showed / a navigation is under way: hold background work
 *  off for `ms` (longer than the entrance animation). */
export function markBusy(ms: number): void {
  busyUntil = Math.max(busyUntil, now() + ms);
}

/** Bracket a job the player is actively waiting on (a race launch under its
 *  splash): while any foreground job runs, the gate never holds anything. */
export function beginForeground(): void {
  foreground++;
}
export function endForeground(): void {
  foreground = Math.max(0, foreground - 1);
}

/** A race is being driven: background jobs (garage pre-warm, boot warm-up,
 *  the account chunk) hold off entirely — and `whenIdle`'s max wait does not
 *  count down while it lasts, so nothing lands mid-drive by timeout. */
export function setRaceActive(on: boolean): void {
  raceActive = on;
}

/** True when no input landed in the last `quietMs` and no navigation is in
 *  flight (or a foreground job is running). */
export function isQuiet(quietMs = 300): boolean {
  if (foreground > 0) return true;
  if (raceActive) return false;
  const t = now();
  return t - lastInputAt >= quietMs && t >= busyUntil;
}

/**
 * Resolve once the player has been quiet for `quietMs` — or after
 * `maxWaitMs`, so a player who taps around forever still eventually gets
 * the warm-up instead of never. Polls (no listeners to leak).
 */
export function whenIdle(
  quietMs = 300,
  maxWaitMs = 4000,
  opts: {
    /** Default true: a live race keeps deferring (the max wait never counts
     *  down mid-drive). Pass false for work the race itself may NEED — a
     *  landmark model still landing after an online race began must parse
     *  within `maxWaitMs`, stutter or not, rather than stay missing. */
    holdDuringRace?: boolean;
  } = {},
): Promise<void> {
  const hold = opts.holdDuringRace !== false;
  const quiet = (): boolean => (hold ? isQuiet(quietMs) : isQuietIgnoringRace(quietMs));
  if (quiet()) return Promise.resolve();
  let deadline = now() + maxWaitMs;
  return new Promise((resolve) => {
    const poll = (): void => {
      if (hold && raceActive && foreground === 0) deadline = now() + maxWaitMs;
      if (quiet() || now() >= deadline) resolve();
      else setTimeout(poll, 50);
    };
    setTimeout(poll, 50);
  });
}

function isQuietIgnoringRace(quietMs: number): boolean {
  if (foreground > 0) return true;
  const t = now();
  return t - lastInputAt >= quietMs && t >= busyUntil;
}

/** Install the input listeners once (main.ts). Safe to call repeatedly. */
export function installIdleGate(): void {
  if (installed || typeof document === 'undefined') return;
  installed = true;
  const opts: AddEventListenerOptions = { capture: true, passive: true };
  document.addEventListener('pointerdown', noteInput, opts);
  document.addEventListener('pointerup', noteInput, opts);
  document.addEventListener('keydown', noteInput, opts);
  document.addEventListener('touchstart', noteInput, opts);
}

/** Test hook: reset all state. */
export function resetIdleGate(): void {
  lastInputAt = -1e9;
  busyUntil = 0;
  foreground = 0;
  raceActive = false;
}
